/**
 * Ideas of a workspace in the cloud, and who is told about their tasks.
 *
 *   projects/{projectId}/ideas/{ideaId}    a card (utils/ideas.ts)
 *   notifications/{id}                     what the bell shows someone given a task, or whose task was done
 *
 * Who sees a card follows from the workspace: its owner and the people it is shared with
 * (the workspace document's `sharedWith`). Everything is written straight to the database
 * under its rules (firestore.rules, "Ideas of a workspace"); there is no server function.
 * A card's words are saved as they are; every other change is one transaction on the card
 * as it is NOW, so two people reacting, keeping or giving at once do not write over each other.
 */
import { getFirestore, collection, doc, onSnapshot, getDoc, setDoc, updateDoc, deleteDoc, runTransaction, writeBatch } from 'firebase/firestore';
import { getFirebaseApp, isFirebaseActive, withCloudTimeout, describeCloudError } from './storage';
import {
    addRun, assignNotices, doneNotices, giveIdea, newIdea, setAsk, setDone, setLocked, setName, setScreenplay, setText, toIdea, toggleReaction,
    type AskRun, type Idea, type IdeaAsk, type IdeaNotice, type IdeaSource, type MadeBy, type NewIdeaInput, type WorkspacePerson,
} from './ideas';
import type { ScreenplayMeta } from './screenplay';
import type { PromptFor } from './promptWriter';

/** The signed-in person, as ideas need them */
export interface IdeaUser { id: string; name: string; email: string; picture?: string }

export const ideasAvailable = (): boolean => isFirebaseActive();

const db = () => {
    const app = getFirebaseApp();
    if (!app) throw new Error('Ideas need the cloud. Sign in to use them.');
    return getFirestore(app);
};

/** Run a cloud call with the offline and timeout guard, and a message a person can read. */
const cloud = async <T,>(label: string, run: () => Promise<T>, fallback: string = 'That did not go through'): Promise<T> => {
    try {
        return await withCloudTimeout(run);
    } catch (e) {
        console.error(`[ideas] ${label} failed:`, e);
        throw new Error(describeCloudError(e, fallback));
    }
};

/** The address a notification opens: the workspace's Ideas page, on the card. */
export const ideaUrl = (projectId: string, ideaId: string): string =>
    `/workspaces?open=${encodeURIComponent(projectId)}&view=ideas${ideaId ? `&idea=${encodeURIComponent(ideaId)}` : ''}`;

const ideaRef = (projectId: string, ideaId: string) => doc(db(), 'projects', projectId, 'ideas', ideaId);

// ── Who is in a workspace ────────────────────────────────────────────────────

/**
 * The people of a workspace: its owner, then everyone it is shared with. Read from the
 * workspace's own record (which the owner and the people it is shared with may read).
 * The owner's name is on the record once they have saved with a version that writes it;
 * `ownerName` stands in before that, unless `me` is them.
 */
export const readWorkspacePeople = async (projectId: string, me: IdeaUser, ownerName?: string | null): Promise<WorkspacePerson[]> => {
    const snapshot = await cloud('people', () => getDoc(doc(db(), 'projects', projectId)), 'The people of this workspace could not be read');
    if (!snapshot.exists()) throw new Error('This workspace is not in the cloud.');
    const data = snapshot.data() as Record<string, any>;
    const ownerId: string = data.ownerId;
    const owner: WorkspacePerson = ownerId === me.id
        ? { id: me.id, name: me.name, email: me.email, ...(me.picture ? { picture: me.picture } : {}), permission: 'owner' }
        : { id: ownerId, name: (typeof data.ownerName === 'string' && data.ownerName) || ownerName || 'The owner', email: '', permission: 'owner' };
    const shared = Object.entries((data.sharedWith && typeof data.sharedWith === 'object' ? data.sharedWith : {}) as Record<string, any>)
        .filter(([id]) => id !== ownerId)
        .map(([id, entry]): WorkspacePerson => ({
            id,
            name: id === me.id ? me.name : (entry?.name || entry?.email || 'Someone'),
            email: id === me.id ? me.email : (entry?.email || ''),
            permission: entry?.permission === 'edit' ? 'edit' : 'view',
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    return [owner, ...shared];
};

// ── Listening ────────────────────────────────────────────────────────────────

type Stop = () => void;

/** Every card of a workspace, as they change */
export const watchIdeas = (projectId: string, onIdeas: (ideas: Idea[]) => void, onError: (message: string) => void): Stop =>
    onSnapshot(
        collection(db(), 'projects', projectId, 'ideas'),
        (snapshot) => onIdeas(snapshot.docs.map(d => toIdea(d.id, projectId, d.data() as Record<string, any>))),
        (error) => { console.error('[ideas] listener failed:', error); onError(describeCloudError(error, 'The ideas could not be loaded')); },
    );

// ── Telling people ───────────────────────────────────────────────────────────

/**
 * Put notices in the bell of the people they are for, among those with access to the
 * workspace. Never fails the change: the card has changed whether or not everyone could be told.
 */
const sendNotices = async (idea: Pick<Idea, 'id' | 'projectId'>, people: ReadonlyArray<WorkspacePerson>, notices: IdeaNotice[], actorId: string): Promise<void> => {
    const ids = people.map(person => person.id);
    const wanted = notices.filter(notice => notice.to !== actorId && ids.includes(notice.to));
    if (wanted.length === 0) return;
    try {
        const now = Date.now();
        const batch = writeBatch(db());
        for (const notice of wanted) {
            batch.set(doc(collection(db(), 'notifications')), {
                userId: notice.to, type: notice.kind, title: notice.title, body: notice.body,
                data: { projectId: idea.projectId, ideaId: idea.id }, read: false, actionUrl: ideaUrl(idea.projectId, idea.id), createdAt: now,
            });
        }
        await withCloudTimeout(() => batch.commit());
    } catch (e) {
        console.warn('[ideas] could not notify:', e);
    }
};

// ── Cards ────────────────────────────────────────────────────────────────────

const stored = (idea: Idea) => { const { id: _id, projectId: _p, ...data } = idea; return data; };

/** A new card, written at once */
export const createIdea = async (projectId: string, user: IdeaUser, input: NewIdeaInput = {}): Promise<Idea> => {
    const idea = newIdea(projectId, { id: user.id, name: user.name }, Date.now(), input);
    await cloud('create idea', () => setDoc(ideaRef(projectId, idea.id), stored(idea)), 'The card could not be made');
    return idea;
};

/** Several cards at once (what a model wrote), in one write; the first is the newest */
export const createIdeas = async (
    projectId: string,
    user: IdeaUser,
    cards: { name: string; text: string; kind?: Idea['kind']; screenplay?: Partial<ScreenplayMeta>; at?: { x: number; y: number } | null }[],
    source: IdeaSource,
    about: { fromAskId?: string | null; madeBy?: MadeBy | null; prompt?: string | null; promptFor?: PromptFor | null } = {},
): Promise<Idea[]> => {
    const now = Date.now();
    // Each a moment older than the one before, so the board keeps their order
    const ideas = cards.map((card, i) => newIdea(projectId, { id: user.id, name: user.name }, now - i, { ...card, at: card.at ?? null, source, fromAskId: about.fromAskId ?? null, madeBy: about.madeBy ?? null, prompt: about.prompt ?? null, promptFor: about.promptFor ?? null }));
    if (ideas.length === 0) return [];
    await cloud('create ideas', async () => {
        const batch = writeBatch(db());
        for (const idea of ideas) batch.set(ideaRef(projectId, idea.id), stored(idea));
        await batch.commit();
    }, 'The cards could not be made');
    return ideas;
};

/** The words of a card (its text, its name, what a Think up card asks, a screenplay's title page and versions), saved as they are */
export const saveIdeaWords = async (projectId: string, ideaId: string, words: { text?: string; name?: string; ask?: Partial<IdeaAsk>; screenplay?: ScreenplayMeta; kind?: Idea['kind'] }): Promise<void> => {
    const now = Date.now();
    const kind = words.kind ?? (words.ask ? 'ask' : words.screenplay ? 'screenplay' : 'card');
    const blank = newIdea(projectId, { id: '', name: '' }, 0, { kind });
    const fields: Record<string, unknown> = { updatedAt: now };
    if (words.text !== undefined) fields.text = setText(blank, words.text, now).text;
    if (words.name !== undefined) fields.name = setName(blank, words.name, now).name;
    if (words.screenplay !== undefined) fields.screenplay = setScreenplay(blank, words.screenplay, now).screenplay;
    if (words.ask !== undefined) {
        // Only the fields given, so what someone else set meanwhile stays
        const cleaned = setAsk(blank, words.ask, now).ask!;
        for (const key of Object.keys(words.ask) as (keyof IdeaAsk)[]) fields[`ask.${key}`] = cleaned[key];
    }
    await cloud('save idea', () => updateDoc(ideaRef(projectId, ideaId), fields), 'The card could not be saved');
};

/** Where a card was put on the board, saved for everyone who sees it */
export const saveIdeaPlace = async (projectId: string, ideaId: string, at: { x: number; y: number } | null): Promise<void> => {
    const placed = at ? { x: Math.round(at.x), y: Math.round(at.y) } : null;
    await cloud('place idea', () => updateDoc(ideaRef(projectId, ideaId), { at: placed, updatedAt: Date.now() }), 'The card could not be moved');
};

/**
 * Where several cards were put, in one write (per 400): the first move on a board the page
 * arranged keeps every card where it was, so nothing else moves from then on.
 */
export const saveIdeaPlaces = async (projectId: string, places: ReadonlyArray<{ id: string; at: { x: number; y: number } | null }>): Promise<void> => {
    if (places.length === 0) return;
    const now = Date.now();
    await cloud('place ideas', async () => {
        for (let i = 0; i < places.length; i += 400) {
            const batch = writeBatch(db());
            for (const { id, at } of places.slice(i, i + 400)) {
                batch.update(ideaRef(projectId, id), { at: at ? { x: Math.round(at.x), y: Math.round(at.y) } : null, updatedAt: now });
            }
            await batch.commit();
        }
    }, 'The cards could not be moved');
};

/** One asking of a Think up card remembered on it, with what it cost */
export const recordAskRun = (projectId: string, askId: string, run: AskRun): Promise<Idea> =>
    changeIdea(projectId, askId, (idea) => addRun(idea, run));

/**
 * A change to a card, made on the card as it is now (one transaction). Resolves with the card
 * as it is after the change; the same card when the change was nothing.
 */
export const changeIdea = async (projectId: string, ideaId: string, change: (idea: Idea, now: number) => Idea): Promise<Idea> =>
    cloud('change idea', () => runTransaction(db(), async (transaction) => {
        const snapshot = await transaction.get(ideaRef(projectId, ideaId));
        if (!snapshot.exists()) throw new Error('This card has been deleted.');
        const idea = toIdea(snapshot.id, projectId, snapshot.data() as Record<string, any>);
        const next = change(idea, Date.now());
        if (next !== idea) {
            const { id: _id, projectId: _p, ...data } = next;
            const patch: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(data)) if (JSON.stringify(value) !== JSON.stringify((idea as any)[key])) patch[key] = value;
            transaction.update(ideaRef(projectId, ideaId), patch);
        }
        return next;
    }), 'The card could not be changed');

export const reactToIdea = (projectId: string, ideaId: string, emoji: string, user: IdeaUser): Promise<Idea> =>
    changeIdea(projectId, ideaId, (idea, now) => toggleReaction(idea, emoji, user.id, now));

export const keepIdea = (projectId: string, ideaId: string, locked: boolean, user: IdeaUser): Promise<Idea> =>
    changeIdea(projectId, ideaId, (idea, now) => setLocked(idea, locked, user, now));

/** Give a card to one of the workspace's people (or to nobody), and tell them */
export const giveIdeaTo = async (people: ReadonlyArray<WorkspacePerson>, projectId: string, ideaId: string, personId: string | null, user: IdeaUser): Promise<Idea> => {
    const person = personId ? people.find(p => p.id === personId) : null;
    if (personId && !person) throw new Error('That person no longer has access to this workspace.');
    const idea = await changeIdea(projectId, ideaId, (current, now) => giveIdea(current, person ? { id: person.id, name: person.name } : null, now));
    void sendNotices(idea, people, assignNotices(idea, { id: user.id, name: user.name }), user.id);
    return idea;
};

/** Tick a card done (or back to open), and tell whoever wrote it */
export const finishIdea = async (people: ReadonlyArray<WorkspacePerson>, projectId: string, ideaId: string, done: boolean, user: IdeaUser): Promise<Idea> => {
    const idea = await changeIdea(projectId, ideaId, (current, now) => setDone(current, done, user, now));
    if (done) void sendNotices(idea, people, doneNotices(idea, { id: user.id, name: user.name }), user.id);
    return idea;
};

/** Delete a card for good */
export const deleteIdea = async (projectId: string, ideaId: string): Promise<void> => {
    await cloud('delete idea', () => deleteDoc(ideaRef(projectId, ideaId)), 'The card could not be deleted');
};
